import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { GroupBase, MultiValueProps } from 'react-select';
import { useElementRef } from '@tanium/coreui-utils';
import styled from '@tanium/react-emotion';
import { CloseIcon } from '@tanium/react-graphics';
import { IconButton } from '@tanium/react-icon-button';
import { PillBase } from '@tanium/react-pill';
import { Stack } from '@tanium/react-stack';
import { ThemeDefinition } from '@tanium/react-theme-definition';
import { useAggregatedRef } from '@tanium/react-use-aggregated-ref';
import { useResizeObserver } from '@tanium/react-use-resize-observer';
import { css } from '@tanium/style-system';
import { ThemeSpace } from '@tanium/theme-data';
import { MultiCollapsibleContext } from './createValueContainer';
import { fixMultiValueRemoveElementProps } from './fixMultiValueRemoveElementProps';
import { useTranslation } from './localization';
import { RemainingPillCount } from './themeDefinitions';
import { SelectSize } from './types';

const StyledIconButton = styled(IconButton)({ display: 'block' });

const RemoveButtonContainer = styled('span')(
  css({
    paddingLeft: ThemeSpace.minor[1],
  }),
);

const DEFAULT_MAX_MAX_WIDTH = 200;
const minMaxWidth = 50;

const TruncatedText = styled('div', {
  shouldForwardProp: (p) => p !== 'textColor',
})<{ textColor?: ThemeDefinition }>(({ textColor: color }) =>
  css({
    color,
    fontWeight: 'bold',
    lineHeight: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
);

export const createMultiValuePill =
  <Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>(
    size: SelectSize,
    enableMaxWidth: boolean,
  ) =>
  (props: MultiValueProps<Option, IsMulti, Group>) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const value = props.selectProps.getOptionLabel!(props.data);
    const { getValue, isDisabled } = props;
    const allValues = getValue();
    const currentPillIndex = allValues.findIndex((item) => item === props.data);
    const maxMaxWidth = enableMaxWidth ? DEFAULT_MAX_MAX_WIDTH : Infinity;
    const [maxWidth, setMaxWidth] = useState(maxMaxWidth);
    const containerRef = useRef<HTMLElement>(null);
    const { element: textContainerElement, ref: textContainerRef } = useElementRef<HTMLElement>();
    const { element: textValueElement, ref: textValueRef } = useElementRef<HTMLElement>();
    const [shouldForceTooltip, setShouldForceTooltip] = useState(false);
    const removeButtonSizePx = size === 'lg' ? 14 : 12;
    const { t } = useTranslation();

    const { maxPills = allValues.length } = useContext(MultiCollapsibleContext);

    const showPill = currentPillIndex < maxPills;
    const showCollapsedPills = currentPillIndex + 1 === maxPills && allValues.length > maxPills;
    // BasePill supports a maxWidth for ellipsizing its text, but Select elements are not width bound
    // as they are defined by us, so we need to be able to dynamically set the maxWidth of our pills
    const updateMaxWidth = useCallback(
      (parent: Element | null) => {
        const element = containerRef.current;
        if (!element) {
          return;
        }
        if (!parent) {
          setMaxWidth(maxMaxWidth);
          return;
        }
        const elementComputedStyle = getComputedStyle(element);
        const textContainerWidth = textContainerElement?.offsetWidth ?? 0;
        const removeButtonWidth = removeButtonSizePx ?? 0;
        const textValueWidth = textValueElement?.getBoundingClientRect().width ?? 0;
        const nonContentWidth = Math.max(
          element.offsetWidth - textContainerWidth + removeButtonWidth,
          0,
        );
        const usableParentWidth =
          parent.clientWidth -
          parseFloat(elementComputedStyle.paddingLeft) -
          parseFloat(elementComputedStyle.paddingRight);
        const newMaxWidth = Math.min(
          Math.max(usableParentWidth - nonContentWidth, minMaxWidth),
          maxMaxWidth,
        );
        setMaxWidth(newMaxWidth);
        setShouldForceTooltip(newMaxWidth > usableParentWidth || textValueWidth > newMaxWidth);
      },
      [textContainerElement?.offsetWidth, removeButtonSizePx, textValueElement, maxMaxWidth],
    );

    const parentElementRef = useResizeObserver(
      useCallback(
        (e) => {
          updateMaxWidth(e.target);
        },
        [updateMaxWidth],
      ),
    );

    const combinedContainerRef = useAggregatedRef(
      useMemo(
        () => [
          containerRef,
          (node: HTMLElement | null) => {
            parentElementRef(node?.offsetParent || null);
          },
        ],
        [parentElementRef],
      ),
    );

    const remainingCount = allValues.length - maxPills;

    return showPill ? (
      <Stack ref={combinedContainerRef} m="2px" itemSpacing={1 / 2}>
        <PillBase
          tooltipContent={value}
          maxWidth={maxWidth}
          alwaysEnableTooltip={shouldForceTooltip}
          mainContentRef={textContainerRef}
          size={size}
          disabled={isDisabled}
          trailingContent={
            <RemoveButtonContainer>
              <StyledIconButton
                icon={CloseIcon}
                height={removeButtonSizePx}
                width={removeButtonSizePx}
                label={t('Remove')}
                disabled={isDisabled}
                // removeProps includes an onMouseDown handler that prevents the containing menu from
                // opening/closing when the user removes an item. Use caution when refactoring.
                {...fixMultiValueRemoveElementProps(props.removeProps)}
              />
            </RemoveButtonContainer>
          }
        >
          <TruncatedText ref={textValueRef}>{value}</TruncatedText>
        </PillBase>
        {showCollapsedPills && (
          <PillBase
            alwaysEnableTooltip
            tooltipContent={t(
              '{{count, INTEGER}} more selected item not shown. Click to expand and view.',
              {
                count: remainingCount,
              },
            )}
            size={size}
          >
            <TruncatedText textColor={RemainingPillCount}>
              {t('+{{count, INTEGER}}', { count: remainingCount })}
            </TruncatedText>
          </PillBase>
        )}
      </Stack>
    ) : null;
  };
